import { User } from '../types';
import { cn } from './ui/utils';
import {
  LayoutDashboard,
  Package,
  ClipboardList,
  Users,
  History,
  Settings,
  FileText,
  MessageSquare,
  Medal,
  Dumbbell,
  IdCard
} from 'lucide-react';

interface NavigationProps {
  user: User;
  currentView: string;
  onNavigate: (view: string) => void;
}

export function Navigation({ user, currentView, onNavigate }: NavigationProps) {
  const adminNavItems = [
    { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { id: 'student-data-bank', label: 'Student Data Bank', icon: IdCard },
    { id: 'equipment', label: 'Equipment Management', icon: Package },
    { id: 'requests', label: 'Borrow Requests', icon: ClipboardList },
    { id: 'users', label: 'User Management', icon: Users },
    { id: 'documents', label: 'Document Management', icon: FileText },
    { id: 'sports-management', label: 'Sports Management', icon: Dumbbell },
    { id: 'feedback', label: 'User Feedback', icon: MessageSquare },
    { id: 'history', label: 'Transaction History', icon: History },
    { id: 'settings', label: 'Settings', icon: Settings }
  ];

  const coachNavItems = [
    { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { id: 'sports-evaluation', label: 'Sports Evaluation', icon: Medal },
    { id: 'browse', label: 'Browse Equipment', icon: Package },
    { id: 'my-requests', label: 'My Requests', icon: ClipboardList },
    { id: 'forms', label: 'Forms & Documents', icon: FileText },
    { id: 'history', label: 'My History', icon: History }
  ];

  const studentNavItems = [
    { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { id: 'browse', label: 'Browse Equipment', icon: Package },
    { id: 'my-requests', label: 'My Requests', icon: ClipboardList },
    { id: 'forms', label: 'Forms & Documents', icon: FileText },
    { id: 'sports-registration', label: 'Sports Registration', icon: Medal },
    { id: 'history', label: 'My History', icon: History }
  ];

  const facultyNavItems = [
    { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { id: 'browse', label: 'Browse Equipment', icon: Package },
    { id: 'my-requests', label: 'My Requests', icon: ClipboardList },
    { id: 'forms', label: 'Forms & Documents', icon: FileText },
    { id: 'history', label: 'My History', icon: History }
  ];

  const navItems =
    user.role === 'admin'
      ? adminNavItems
      : user.role === 'coach'
      ? coachNavItems
      : user.role === 'student'
      ? studentNavItems
      : facultyNavItems;

  return (
    <nav className="p-3 sm:p-4 space-y-1.5 text-white">
      {/* Navigation Header */}
      <div className="px-3 py-3 mb-2">
        <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground/70">
          Navigation
        </p>
      </div>

      {navItems.map((item) => {
        const Icon = item.icon;
        const isActive = currentView === item.id;

        return (
          <button
            key={item.id}
            onClick={() => onNavigate(item.id)}
            className={cn(
              'group relative w-full flex items-center gap-3 px-3 sm:px-4 py-2.5 sm:py-3 rounded-xl transition-all duration-200',
              isActive
                ? 'bg-primary text-primary-foreground shadow-md'
                : 'hover:bg-accent/60 hover:shadow-sm text-foreground'
            )}
          >
            {/* Left active indicator */}
            {isActive && (
              <span className="absolute left-0 top-1/2 -translate-y-1/2 h-6 w-1 bg-primary-foreground rounded-r-full animate-slideIn" />
            )}

            {/* Icon with animation */}
            <Icon
              className={cn(
                'h-4 w-4 sm:h-5 sm:w-5 flex-shrink-0 transition-transform duration-200',
                isActive ? 'scale-110' : 'group-hover:scale-110'
              )}
            />

            {/* Text */}
            <span className="truncate text-sm sm:text-base font-medium tracking-wide leading-snug">
              {item.label}
            </span>
          </button>
        );
      })}
    </nav>
  );
}
